import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import { useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import BigCalendar from '@/components/BigCalendar';
import type { CalendarEvent, CalendarView } from '@/components/BigCalendar/types';
import { getVisibleRange, isSameDayInTimeZone } from '@/components/BigCalendar/utils/dateGrid';
import { useAppointmentCalendar } from '@/hooks/useAppointmentCalendar';
import useNavigateToPage from '@/hooks/useNavigateToPage';
import { Button, Select, Spinner, Toggle } from '@/ui';
import { getBrowserTimeZone, isValidTimeZone } from '@/utils/dates';

import { appointmentsToCalendarEvents } from './utils/appointmentToCalendarEvent';
import { calendarTimeZoneOptions } from './utils/calendarTimeZoneOptions';

const CALENDAR_VIEWS: CalendarView[] = ['day', 'week', 'month', 'agenda'];

const parseView = (value: string | null): CalendarView =>
  CALENDAR_VIEWS.includes(value as CalendarView) ? (value as CalendarView) : 'week';

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

// A bare `YYYY-MM-DD` is a day in `timeZone`; `new Date` would read it as UTC
// midnight, which is the previous day anywhere west of UTC.
const parseDate = (value: string | null, timeZone: string): Date => {
  if (!value) return new Date();
  const parsed = DATE_ONLY.test(value) ? fromZonedTime(value, timeZone) : new Date(value);
  return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
};

const Calendar = () => {
  const navigate = useNavigate();
  const { navigateToAppointmentById } = useNavigateToPage();
  const [searchParams, setSearchParams] = useSearchParams();

  const view = parseView(searchParams.get('view'));
  const hideCancelled = searchParams.get('hideCancelled') === 'true';

  const browserTimeZone = useMemo(getBrowserTimeZone, []);
  const requestedTimeZone = searchParams.get('tz');
  const timeZone =
    requestedTimeZone && isValidTimeZone(requestedTimeZone) ? requestedTimeZone : browserTimeZone;
  const timeZoneOptions = useMemo(
    () => calendarTimeZoneOptions(browserTimeZone, timeZone),
    [browserTimeZone, timeZone]
  );
  const selectedTimeZoneOption = timeZoneOptions.find((option) => option.value === timeZone);
  const date = parseDate(searchParams.get('date'), timeZone);

  const setView = useCallback(
    (nextView: CalendarView) =>
      setSearchParams((params) => {
        params.set('view', nextView);
        return params;
      }),
    [setSearchParams]
  );

  const setDate = useCallback(
    (nextDate: Date) =>
      setSearchParams((params) => {
        params.set('date', formatInTimeZone(nextDate, timeZone, 'yyyy-MM-dd'));
        return params;
      }),
    [setSearchParams, timeZone]
  );

  const setHideCancelled = useCallback(
    (next: boolean) =>
      setSearchParams((params) => {
        params.set('hideCancelled', String(next));
        return params;
      }),
    [setSearchParams]
  );

  const setTimeZone = useCallback(
    (option: { value: string } | null) =>
      setSearchParams((params) => {
        if (option) params.set('tz', option.value);
        else params.delete('tz');
        return params;
      }),
    [setSearchParams]
  );

  const { start, end } = useMemo(
    () => getVisibleRange(date, view, timeZone),
    [date, view, timeZone]
  );

  const {
    data: appointments,
    isLoading,
    isFetching
  } = useAppointmentCalendar({
    start: start.toISOString(),
    end: end.toISOString()
  });

  const events = useMemo(() => {
    const allEvents = appointmentsToCalendarEvents(appointments ?? []);
    return hideCancelled ? allEvents.filter((event) => !event.isCancelled) : allEvents;
  }, [appointments, hideCancelled]);

  const handleEventClick = useCallback(
    (event: CalendarEvent) => navigateToAppointmentById(event.id),
    [navigateToAppointmentById]
  );

  return (
    <div className="flex h-screen w-full flex-col p-4">
      <div className="flex items-center justify-between pb-2">
        <Button
          aria-label="Go back"
          variant="ghost"
          color="neutral"
          size="icon-md"
          leftIcon={faChevronLeft}
          onClick={() => navigate(-1)}
        />
        <div className="flex items-center gap-3">
          {isFetching && !isLoading && <Spinner size="sm" label="Refreshing appointments" />}
          <Select
            ariaLabel="Time zone"
            className="w-56"
            clearable={false}
            options={timeZoneOptions}
            selectedOption={selectedTimeZoneOption}
            onChange={setTimeZone}
          />
          <Toggle checked={hideCancelled} onChange={setHideCancelled} size="sm">
            Hide cancelled
          </Toggle>
        </div>
      </div>
      {isLoading ? (
        <div className="flex justify-center py-24">
          <Spinner size="lg" label="Loading appointments" />
        </div>
      ) : (
        <BigCalendar
          events={events}
          view={view}
          onViewChange={setView}
          date={date}
          onNavigate={setDate}
          onEventClick={handleEventClick}
          timeZone={timeZone}
          className="min-h-0 flex-1"
          isDateHighlighted={(d) => isSameDayInTimeZone(d, new Date(), timeZone)}
        />
      )}
    </div>
  );
};

export default Calendar;
